// AdministrativeAreaFormDrawer — Add (parent + name) OR Rename (name only), on the
// shared DrawerForm + useFormShell (RHF + Zod). The universal Add: the PARENT on the
// navigator field (ADR-080 §14) — the project's countries at the root (a country's
// name picks its top level), one level at a time, "This area" picks the open one, a
// search answers every level with each match's path — then the name, appended. "Add
// inside" fixes the parent. Rename hides the parent (name is the only edit; the
// parent changes via Move).
// The field's rows are built HERE (`parentRows`, over the page's whole tree), not asked of
// the shared areas source the site form uses: this field alone roots at the project's
// countries, Move leaves a subtree out, and the drawer freezes the tree it is changing
// (below) — the one screen that knows where its areas come from.
// The Rename rides the row stamp (ADR-084): the shell seeds from the area as it is at
// each open, the rename carries the stamp, and a stale save shows the line with Load
// the latest — the tree re-read, the area picked from it.

import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Controller } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { DrawerForm } from '../../../components/patterns/forms/DrawerForm.js';
import { readLatestRow } from '../../../components/patterns/forms/readLatest.js';
import { useFormShell } from '../../../components/patterns/forms/useFormShell.js';
import { Label } from '../../../components/ui/Label.js';
import { Input } from '../../../components/ui/Input.js';
import { Picker } from '../../../components/patterns/Picker.js';
import { Alert } from '../../../components/ui/Alert.js';
import { useCreateAdministrativeArea, useRenameAdministrativeArea } from '../../../hooks/useAdministrativeAreaMutations.js';
import { administrativeAreasKeys, fetchAdministrativeAreas } from '../../../hooks/useAdministrativeAreas.js';
import { useProjectCountries } from '../../../hooks/useProjectCountries.js';
import { useResetOnOpen } from '../../../hooks/useResetOnOpen.js';
import type { CodeSource } from '../../../hooks/useOptions.js';
import { countryName } from '../../../lib/format.js';
import { COUNTRY_VALUE, distinctCountries, parentRows } from './administrativeAreaTree.js';
import type { AdministrativeAreaNode } from '@ems-app/schemas/administrative-areas/responses';

// An Add needs a parent (a country's top level or an area); a Rename has none.
const schema = z
  .object({ adding: z.boolean(), parentValue: z.string(), name: z.string().trim().min(1).max(64) })
  .refine((v) => !v.adding || v.parentValue.length > 0, { path: ['parentValue'] });
type Values = z.infer<typeof schema>;

export interface AdministrativeAreaFormDrawerProps {
  projectId: string;
  /** The whole tree (flat) — for the parent picker + country resolution. */
  allNodes: AdministrativeAreaNode[];
  /** Present → Rename mode (name only). Absent → Add mode. */
  node?: AdministrativeAreaNode | null;
  /** Add mode only: pre-seed the parent ("Add area inside"); null = top level. */
  presetParentId?: number | null;
  /** Add mode only: pre-seed the COUNTRY for a new root (from a country peek's Create). */
  presetCountry?: string;
  open: boolean;
  onClose: () => void;
  onCreated?: (id: number) => void;
}

export function AdministrativeAreaFormDrawer({
  projectId,
  allNodes,
  node,
  presetParentId,
  presetCountry,
  open,
  onClose,
  onCreated,
}: AdministrativeAreaFormDrawerProps) {
  const { t, i18n } = useTranslation('administrative-areas');
  const lang = i18n.language;
  const create = useCreateAdministrativeArea(projectId);
  const rename = useRenameAdministrativeArea(projectId);
  const createdId = useRef<number | null>(null);
  const qc = useQueryClient();
  const isRename = node != null;

  // Freeze the tree for the drawer's OPEN lifetime. The administrativeAreas drawer is
  // SELF-REFERENTIAL: creating a node changes `allNodes` — the very data its parent
  // picker reads. Consuming the live list would (a) shift the picker under a background
  // refetch mid-add, and (b) re-render the drawer's content DURING its close transition
  // (the create commits → invalidates → refetches while the SlideOver is exiting), which
  // wedges React Aria's overlay animation: the ModalOverlay gets stuck with data-entering
  // AND data-exiting both set (getAnimations() never settles) and never unmounts, so the
  // drawer stays open behind the new node's peek. Snapshot on open; re-seed each open (the
  // peek swaps node/preset under a persisted instance). Sibling drawers don't need this —
  // their picker's data (e.g. zones for a cluster) isn't what the create mutates.
  const [pickerNodes, setPickerNodes] = useState(allNodes);
  useResetOnOpen(open, (isOpen) => {
    if (isOpen) setPickerNodes(allNodes);
  });

  const projectCountries = useProjectCountries(projectId);
  // The root = the project's countries (so an EMPTY project can still create its first
  // area) ∪ any country present in the (frozen) tree.
  const countryList = useMemo(
    () => [...new Set([...(projectCountries.data?.map((c) => c.iso_code) ?? []), ...distinctCountries(pickerNodes)])],
    [projectCountries.data, pickerNodes],
  );
  const parentSource = useMemo<CodeSource>(
    () => ({
      kind: 'code',
      key: 'administrative-area-parents',
      rows: parentRows(pickerNodes, { countries: countryList, countryLabel: (iso) => countryName(iso, lang) }),
      pending: projectCountries.isPending,
    }),
    [pickerNodes, countryList, lang, projectCountries.isPending],
  );
  const noCountries = !isRename && presetParentId == null && !presetCountry && countryList.length === 0;
  // A free Add starts at the project's default country's top level (or its only one).
  const defaultCountry = projectCountries.data?.find((c) => c.is_default)?.iso_code ?? (countryList.length === 1 ? countryList[0] : undefined);
  const defaultParent =
    presetParentId != null
      ? String(presetParentId)
      : presetCountry
        ? `${COUNTRY_VALUE}${presetCountry}`
        : defaultCountry
          ? `${COUNTRY_VALUE}${defaultCountry}`
          : '';
  const presetName = presetParentId != null ? (pickerNodes.find((n) => n.id === presetParentId)?.name ?? '') : '';
  // Opened from a peek with a FIXED destination — "Add inside {node}" (a specific parent)
  // or a country's Add (that country's top level). The parent is decided by where you
  // launched from, so the field comes chosen and LOCKED. Only the list's "+ Add" picks freely.
  const parentLocked = presetParentId != null || !!presetCountry;

  // The seed is read at every open (the container re-seeds — the peek swaps the node or the preset under a persisted
  // drawer instance, so Add inside never keeps the previously-viewed node's parent).
  const shell = useFormShell<Values, AdministrativeAreaNode>({
    schema,
    row: node ?? null,
    values: (r) => ({ adding: r === null, parentValue: r === null ? defaultParent : '', name: r?.name ?? '' }),
    reload: node
      ? () => readLatestRow(qc, administrativeAreasKeys.all(projectId), () => fetchAdministrativeAreas(projectId), (l) => l.find((n) => n.id === node.id))
      : undefined,
    onSubmit: async (v, { stamp, changed }) => {
      if (isRename && node) {
        // Only what changed since the drawer opened, with the stamp.
        if ('name' in changed) await rename.mutateAsync({ administrativeAreaId: node.id, body: { row_version: stamp, name: v.name } });
        return;
      }
      let parentId: number | null;
      let country: string;
      if (v.parentValue.startsWith(COUNTRY_VALUE)) {
        parentId = null;
        country = v.parentValue.slice(COUNTRY_VALUE.length);
      } else {
        parentId = Number(v.parentValue);
        country = pickerNodes.find((n) => n.id === parentId)?.country_iso_code ?? '';
      }
      const r = await create.mutateAsync({ parent_id: parentId, country_iso_code: country, name: v.name });
      createdId.current = r.id;
    },
    onSuccess: () => {
      const id = createdId.current;
      createdId.current = null;
      onClose();
      if (id != null) onCreated?.(id);
    },
  });

  // The drawer says what was pressed (one string per act — the row's words), the
  // place it acts on under the title: Rename · <the area>, Add inside · <the parent>,
  // Create administrative area · <the country> (the list's Create names no place).
  const title = isRename ? t('actions.rename') : presetParentId != null ? t('actions.addInside') : t('actions.add');
  const place = isRename ? node?.name : presetParentId != null ? presetName : presetCountry ? countryName(presetCountry, lang) : undefined;

  return (
    <DrawerForm
      shell={shell}
      open={open}
      onClose={onClose}
      title={title}
      description={place || undefined}
      submitLabel={isRename ? t('form.save') : t('form.create')}
      autoFocusField={isRename ? 'name' : undefined}
    >
      {noCountries && (
        <Alert variant="warning" className="mb-3">
          {t('form.noCountries')}
        </Alert>
      )}
      {!isRename && <p className="text-sm text-[var(--color-text-secondary)]">{t('form.addHint')}</p>}
      {!isRename && (
        <Controller
          control={shell.form.control}
          name="parentValue"
          render={({ field }) => (
            <Picker
              name="parentValue"
              source={parentSource}
              navigator={{ thisLevel: t('form.thisArea') }}
              label={t('form.parent')}
              searchPlaceholder={t('form.parentSearch')}
              required
              value={field.value || null}
              onChange={(v) => field.onChange(v ?? '')}
              disabled={parentLocked}
              error={shell.form.formState.errors.parentValue ? ' ' : undefined}
            />
          )}
        />
      )}
      <div>
        <Label htmlFor="geo-name" required>
          {t('form.name')}
        </Label>
        <Input
          id="geo-name"
          {...shell.form.register('name')}
          error={shell.form.formState.errors.name ? ' ' : undefined}
          placeholder={t('form.namePlaceholder')}
        />
      </div>
    </DrawerForm>
  );
}
